import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Send,
  Close,
  Refresh,
  OpenInFull,
  CloseFullscreen,
} from "@mui/icons-material";
import { apiPost } from "../utils";
import { SkuDetailModal } from "../modals/SkuDetailModal";
import { OverdueAccountsModal } from "../modals/OverdueAccountsModal";
import { SlowingDownModal } from "../modals/SlowingDownModal";
import { AtRiskModal } from "../modals/AtRiskModal";
import { CustomersBehindModal } from "../modals/CustomersBehindModal";

const WELCOME =
  "I'm your Profit Matrix assistant. Ask about customer health (churn/RFM), profit segmentation (Defend / Grow / Optimize / Review-Fix), or price-increase opportunities (where / how much / estimated recovery).";

const PANEL_SIZE = {
  normal: { width: 400, height: 550 },
  expanded: { width: 640, height: "min(820px, calc(100vh - 40px))" },
};

const buildChatContext = (filters = {}) => {
  if (!filters || typeof filters !== "object") {
    return {};
  }
  const context = {};
  if (filters.currentPeriodStart) {
    context.current_period_start = filters.currentPeriodStart;
  }
  if (filters.currentPeriodEnd) {
    context.current_period_end = filters.currentPeriodEnd;
  }
  if (filters.priorPeriodStart) {
    context.prior_period_start = filters.priorPeriodStart;
  }
  if (filters.priorPeriodEnd) {
    context.prior_period_end = filters.priorPeriodEnd;
  }
  if (filters.businessUnitId) {
    context.business_unit = filters.businessUnitId;
  }
  if (filters.accountId) {
    context.account = filters.accountId;
  }
  if (filters.productCategoryId) {
    context.product = filters.productCategoryId;
  }
  if (filters.salesPersonId) {
    context.sales_territory = `SP${filters.salesPersonId}`;
  } else if (filters.locationId) {
    context.sales_territory = filters.locationId;
  }
  return context;
};

const FRIENDLY_ERROR =
  "I couldn't complete that just now. Please try asking again in a moment.";

/** Remove model chain-of-thought / thinking tags before display or history. */
const sanitizeBotText = (text) =>
  String(text || "")
    .replace(/<thinking>[\s\S]*?<\/thinking>/gi, "")
    .replace(/<\/?thinking>/gi, "")
    .trim();

// Keep the Converse request small and free of prior error bubbles.
const MAX_HISTORY_MESSAGES = 8;

/** Render light markdown used by the agent: **bold**, *italic*, `code`, newlines. */
const renderChatText = (text, openSkuModal, openOverdueModal, openSlowingDownModal, openAtRiskModal, openCustomersBehindModal, goToCustomerHealth) => {
  if (!text) {
    return null;
  }

  const renderInline = (chunk, keyPrefix) => {
    const nodes = [];
    // [text](url) | **bold** | *italic* | `code` | plain
    const tokenRe = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    let last = 0;
    let match;
    let idx = 0;

    while ((match = tokenRe.exec(chunk)) !== null) {
      if (match.index > last) {
        nodes.push(chunk.slice(last, match.index));
      }
      const token = match[0];
      if (token.startsWith("[")) {
        const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        nodes.push(
          <Box
            component="a"
            href={linkMatch[2]}
            key={`${keyPrefix}-a-${idx}`}
            sx={{ color: "#1976d2", fontWeight: 600, cursor: "pointer" }}
            onClick={(e) => {
              e.preventDefault();
              const href = linkMatch[2];
              // "skus:<accountId>:<stopped|reduced>" opens the SKU detail modal in place
              // instead of navigating - a special scheme the chat response emits, not a
              // real route.
              const skuMatch = href.match(/^skus:(\d+):(stopped|reduced)$/);
              if (skuMatch) {
                openSkuModal({ accountId: skuMatch[1], type: skuMatch[2] });
                return;
              }
              // "overdue-accounts:" opens the full overdue-accounts modal, same live
              // ranking as the chat answer, no scope params needed for now.
              if (href === "overdue-accounts:") {
                openOverdueModal(true);
                return;
              }
              if (href === "slowing-down:") {
                openSlowingDownModal(true);
                return;
              }
              if (href === "at-risk:") {
                openAtRiskModal(true);
                return;
              }
              if (href === "customers-behind:") {
                openCustomersBehindModal(true);
                return;
              }
              // "customer-health:<query>" navigates to the Customer Health
              // page instead of opening a modal - the query string carries
              // the filter/sort this chat answer used, so the full table
              // opens pre-scoped to match.
              if (href.startsWith("customer-health:")) {
                goToCustomerHealth(href.slice("customer-health:".length));
                return;
              }
            }}
          >
            {linkMatch[1]}
          </Box>,
        );
      } else if (token.startsWith("**")) {
        nodes.push(
          <Box
            component="strong"
            key={`${keyPrefix}-b-${idx}`}
            sx={{ fontWeight: 700 }}
          >
            {token.slice(2, -2)}
          </Box>,
        );
      } else if (token.startsWith("*")) {
        nodes.push(
          <Box
            component="em"
            key={`${keyPrefix}-i-${idx}`}
            sx={{ fontStyle: "italic" }}
          >
            {token.slice(1, -1)}
          </Box>,
        );
      } else {
        nodes.push(
          <Box
            component="code"
            key={`${keyPrefix}-c-${idx}`}
            sx={{
              fontFamily: "monospace",
              fontSize: "0.85em",
              px: 0.5,
              bgcolor: "rgba(0,0,0,0.06)",
              borderRadius: 0.5,
            }}
          >
            {token.slice(1, -1)}
          </Box>,
        );
      }
      last = match.index + token.length;
      idx += 1;
    }

    if (last < chunk.length) {
      nodes.push(chunk.slice(last));
    }
    return nodes;
  };

  return String(text)
    .split("\n")
    .map((line, lineIdx) => (
      <Box
        component="span"
        key={`line-${lineIdx}`}
        sx={{ display: "block", minHeight: line.trim() ? undefined : "0.6em" }}
      >
        {renderInline(line, `l${lineIdx}`)}
      </Box>
    ));
};

const toApiMessages = (msgs) => {
  // Only successful turns go to the model (errors / welcome stay UI-only).
  const usable = msgs.filter(
    (msg) =>
      (msg.type === "user" && !msg.error) ||
      (msg.type === "bot" && msg.ok === true),
  );

  const start = usable.findIndex((msg) => msg.type === "user");
  if (start < 0) {
    return [];
  }

  return usable
    .slice(start)
    .slice(-MAX_HISTORY_MESSAGES)
    .map((msg) => ({
      role: msg.type === "user" ? "user" : "assistant",
      content: msg.text,
    }));
};

export const PricingAgentChat = ({ onClose, filters }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  // "Show All ->" carries the exact current/prior window the chat answer
  // used (currentStart/End, priorStart/End) as query params - the
  // Customer Health page itself reads those and syncs its filter bar, so
  // this only has to navigate there and close the chat panel.
  const goToCustomerHealth = (query) => {
    navigate(`/audit/customer-health?${query}`);
    onClose();
  };
  const [skuModal, setSkuModal] = useState(null);
  const [overdueModalOpen, setOverdueModalOpen] = useState(false);
  const [slowingDownModalOpen, setSlowingDownModalOpen] = useState(false);
  const [atRiskModalOpen, setAtRiskModalOpen] = useState(false);
  const [customersBehindModalOpen, setCustomersBehindModalOpen] = useState(false);
  const [messages, setMessages] = useState([{ type: "bot", text: WELCOME }]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  // Ignore stale responses if the user resets mid-request.
  const requestIdRef = useRef(0);
  const panelSize = isExpanded ? PANEL_SIZE.expanded : PANEL_SIZE.normal;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const resetChat = () => {
    requestIdRef.current += 1;
    setIsTyping(false);
    setInputValue("");
    setMessages([{ type: "bot", text: WELCOME }]);
  };

  const handleSend = (text = inputValue) => {
    const trimmed = text.trim();
    if (!trimmed || isTyping) {
      return;
    }

    const requestId = ++requestIdRef.current;
    const newMessages = [...messages, { type: "user", text: trimmed }];
    setMessages(newMessages);
    setInputValue("");
    setIsTyping(true);

    apiPost(
      "v2/agent/chat",
      {
        messages: toApiMessages(newMessages),
        context: buildChatContext(filters),
      },
      (data) => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setIsTyping(false);

        if (data?.success && data?.data?.message) {
          const cleaned = sanitizeBotText(data.data.message);
          setMessages([
            ...newMessages,
            { type: "bot", text: cleaned || data.data.message, ok: true },
          ]);
          return;
        }

        // Demo-safe: friendly copy only; never feed raw errors back into Bedrock history.
        console.error("Customer Health chat failed:", data?.error || data);
        setMessages([
          ...newMessages,
          { type: "bot", text: FRIENDLY_ERROR, error: true },
        ]);
      },
    );
  };

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        bottom: 20,
        right: 20,
        width: panelSize.width,
        height: panelSize.height,
        maxWidth: "calc(100vw - 40px)",
        maxHeight: "calc(100vh - 40px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        overflow: "hidden",
        bgcolor: "#f4f6f8",
        zIndex: 1300,
        transition: "width 0.2s ease, height 0.2s ease",
      }}
    >
      <Box
        sx={{
          p: 2,
          bgcolor: theme.palette.primary.main,
          color: theme.palette.chatbot.title,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            flex: 1,
            fontWeight: "bold",
          }}
        >
          Pricing Coach
        </Typography>
        <Tooltip title={isExpanded ? "Minimize" : "Maximize"}>
          <IconButton
            size="small"
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{ color: "white", mr: 0.5 }}
            aria-label={
              isExpanded ? "Minimize chat panel" : "Maximize chat panel"
            }
          >
            {isExpanded ? (
              <CloseFullscreen fontSize="small" />
            ) : (
              <OpenInFull fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="New chat">
          <IconButton
            size="small"
            onClick={resetChat}
            sx={{ color: "white", mr: 0.5 }}
            disabled={isTyping}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
          <Close />
        </IconButton>
      </Box>

      <Stack
        spacing={2}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
        }}
      >
        {messages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              alignSelf: msg.type === "user" ? "flex-end" : "flex-start",
              maxWidth: "80%",
            }}
          >
            <Paper
              sx={{
                p: 1.5,
                bgcolor:
                  msg.type === "user" ? theme.palette.primary.main : "white",
                color: msg.type === "user" ? "white" : "text.primary",
                borderRadius:
                  msg.type === "user" ? "12px 12px 0 12px" : "12px 12px 12px 0",
              }}
            >
              <Typography
                variant="body2"
                component="div"
                sx={
                  msg.type === "user" ? { whiteSpace: "pre-wrap" } : undefined
                }
              >
                {msg.type === "bot" ? renderChatText(msg.text, setSkuModal, setOverdueModalOpen, setSlowingDownModalOpen, setAtRiskModalOpen, setCustomersBehindModalOpen, goToCustomerHealth) : msg.text}
              </Typography>
            </Paper>
          </Box>
        ))}

        {isTyping && (
          <Box
            sx={{
              alignSelf: "flex-start",
              bgcolor: "white",
              p: 1.5,
              borderRadius: "12px 12px 12px 0",
              boxShadow: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "flex",
                gap: 0.5,
                color: "text.secondary",
              }}
            >
              <span className="typing-dot">●</span>
              <span className="typing-dot">●</span>
              <span className="typing-dot">●</span>
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Stack>

      <Box
        sx={{
          p: 2,
          bgcolor: "white",
          borderTop: "1px solid #ddd",
          display: "flex",
          gap: 1,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Ask about health, segmentation, or price increases..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          disabled={isTyping}
        />
        <IconButton
          color="primary"
          onClick={() => handleSend()}
          disabled={!inputValue.trim() || isTyping}
        >
          <Send />
        </IconButton>
      </Box>
      <SkuDetailModal
        open={!!skuModal}
        accountId={skuModal?.accountId}
        type={skuModal?.type}
        onClose={() => setSkuModal(null)}
      />
      <OverdueAccountsModal
        open={overdueModalOpen}
        onClose={() => setOverdueModalOpen(false)}
      />
      <SlowingDownModal
        open={slowingDownModalOpen}
        onClose={() => setSlowingDownModalOpen(false)}
      />
      <AtRiskModal
        open={atRiskModalOpen}
        onClose={() => setAtRiskModalOpen(false)}
      />
      <CustomersBehindModal
        open={customersBehindModalOpen}
        onClose={() => setCustomersBehindModalOpen(false)}
      />
    </Paper>
  );
};
